import { useContext, useEffect, useState, useMemo } from "react";
import { ScheduleContext } from "context/schedule";
import { ClientContext } from "context/client"
import { ContractContext } from "context/contract";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import Pagination from "@mui/material/Pagination";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Swal from "sweetalert2";
import Joyride, { STATUS, EVENTS } from "react-joyride";

import EventCard from "./components/EventCard";
import CreateEventModal from "layouts/schedule/components/CreateEventModal";
import EventService from "services/event.service";
import HttpService from "services/htttp.service";
import { useMaterialUIController, AuthContext } from "context";

export default function Events() {
    const { schedule, loadEvents, loading } = useContext(ScheduleContext);
    const { clients, loadClients} = useContext(ClientContext);
    const { loadContracts } = useContext(ContractContext);
    const [openModal, setOpenModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    
    const [controller] = useMaterialUIController();
    const { darkMode, sidenavColor } = controller;
    const { user, setUser } = useContext(AuthContext);

    // States for Joyride Tour
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [tourSteps] = useState([
        {
            target: 'body',
            content: 'Gerencie sua agenda de eventos. Visualize, filtre e acompanhe os detalhes de cada contrato.',
            placement: 'center',
            disableBeacon: true,
        },
        {
            target: '#btn-create-event',
            content: 'Clique aqui para adicionar um novo evento manualmente à sua agenda.',
        },
        {
            target: '#event-filter',
            content: 'Use este filtro para visualizar eventos por status (Confirmados, Pendentes, etc).',
        },
        {
            target: '#event-card-0',
            content: 'Aqui está o cartão do evento. Ele exibe data, local, cliente e status.',
        },
    ]);

    // Dummy Event for Tour
    const dummyEvent = {
        id: 'tour-dummy',
        title: 'Aniversário Exemplo',
        event_date: new Date().toISOString(),
        start_time: '19:00',
        end_time: '23:00',
        status: 'confirmed',
        location: 'Salão de Festas Exemplo',
        city: 'São Paulo',
        price: 2500,
        client_id: null // Para não quebrar a busca de cliente
    };

    useEffect(() => {
        if (user && !user.tours?.events) {
            setRunTour(true);
        }
    }, [user]);

    const handleJoyrideCallback = (data) => {
        const { status, type, index, action } = data;

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            setStepIndex(index + (action === "prev" ? -1 : 1));
        } else if (type === EVENTS.TOUR_START) {
            setStepIndex(0);
        }

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            setRunTour(false);
            setStepIndex(0);
            HttpService.post("my-tours/events");
            if (setUser) {
                setUser((prev) => ({
                    ...prev,
                    tours: { ...prev?.tours, events: 1 },
                }));
            }
        }
    };

    useEffect(() => {
        loadEvents();
        loadContracts(); // lista geral
    }, []); // Apenas na montagem do componente

    const filteredEvents = useMemo(() => {
        let data = [...schedule];

        // 1. Ordenação: Passados no fim, Futuros por data (mais próximo)
        const now = new Date();
        const today = new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().split("T")[0];

        data.sort((a, b) => {
            const dateA = a.event_date || "";
            const dateB = b.event_date || "";

            const isPastA = dateA < today;
            const isPastB = dateB < today;

            if (isPastA && !isPastB) return 1;
            if (!isPastA && isPastB) return -1;

            // Se ambos forem futuros (ou hoje): Ascendente
            // Se ambos forem passados: Descendente (mais recente primeiro)
            return !isPastA ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
        });

        // 2. Filtro por Status
        if (statusFilter !== "all") {
            data = data.filter((event) => event.status === statusFilter);
        } else {
            data = data.filter((event) => event.status !== "cancelled");
        }

        return data;
    }, [schedule, statusFilter]);

    // Resetar página ao mudar filtro
    useEffect(() => {
        setPage(1);
    }, [statusFilter]);

    // Lógica de Paginação
    const paginatedEvents = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        return filteredEvents.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredEvents, page]);

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    
    const handleEditEvent = (event) => {
        if (event.id === 'tour-dummy') {
            Swal.fire("Modo Tutorial", "Este é um evento de exemplo e não pode ser editado.", "info");
            return;
        }
        setSelectedEvent(event);
        setOpenModal(true);
    };

    const handleCreateEvent = () => {
        setSelectedEvent(null);
        setOpenModal(true);
    };

    const handleCancelEvent = async (event) => {
        if (event.id === 'tour-dummy') {
            return; 
        }

        const result = await Swal.fire({
            title: "Cancelar evento?",
            text: "O evento será marcado como cancelado. O histórico será mantido.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, cancelar",
            cancelButtonText: "Voltar",
        });

        if (!result.isConfirmed) return;

        try {
            await EventService.cancelEvent(event.id);

            Swal.fire({
                icon: "success",
                title: "Evento cancelado",
                timer: 1500,
                showConfirmButton: false,
            });

            loadEvents();
            loadContracts();
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Erro ao cancelar",
                text: error?.message || "Erro inesperado",
            });
        }
    };

    // Lógica para exibir o Dummy Event durante o tour se a lista estiver vazia
    const eventsToRender = (runTour && paginatedEvents.length === 0) ? [dummyEvent] : paginatedEvents;


    return (
        <DashboardLayout>
            <Joyride
                steps={tourSteps}
                run={runTour}
                stepIndex={stepIndex}
                continuous
                showSkipButton
                showProgress={false}
                callback={handleJoyrideCallback}
                styles={{
                    options: {
                        zIndex: 10000,
                        primaryColor: sidenavColor === 'default' ? '#1A73E8' : 'var(--mui-palette-' + sidenavColor + '-main, #1A73E8)',
                    },
                }}
                locale={{
                    back: 'Voltar',
                    close: 'Fechar',
                    last: 'Finalizar',
                    next: `Próximo (${stepIndex + 1} de ${tourSteps.length})`,
                    skip: 'Pular'
                }}
            />
            <DashboardNavbar />

            <MDBox p={{ xs: 0.5, md: 3 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <MDTypography variant="h4">
                        Eventos
                    </MDTypography>
                    <MDButton id="btn-create-event" variant="gradient" color={sidenavColor} onClick={handleCreateEvent}>
                        Novo Evento
                    </MDButton>
                </MDBox>

                <MDBox mb={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl 
                                fullWidth 
                                size="small" 
                                sx={{ 
                                    backgroundColor: darkMode ? "transparent" : "white", 
                                    borderRadius: 1,
                                    "& .MuiOutlinedInput-root": { color: darkMode ? "white" : "inherit" },
                                    "& .MuiInputLabel-root": { color: darkMode ? "white" : "inherit" },
                                    "& .MuiSelect-icon": { color: darkMode ? "white" : "inherit" },
                                }}
                            >
                                <InputLabel id="status-select-label">Filtrar por Status</InputLabel>
                                <Select
                                    id="event-filter"
                                    labelId="status-select-label"
                                    value={statusFilter}
                                    label="Filtrar por Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    sx={{ height: 44 }}
                                >
                                    <MenuItem value="all">Todos</MenuItem>
                                    <MenuItem value="draft">Rascunho</MenuItem>
                                    <MenuItem value="confirmed">Confirmado</MenuItem>
                                    <MenuItem value="completed">Concluído</MenuItem>
                                    <MenuItem value="cancelled">Cancelado</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </MDBox>

                {loading && <MDTypography>Carregando...</MDTypography>}

                {!loading && eventsToRender.length === 0 && (
                    <MDTypography>Nenhum evento encontrado</MDTypography>
                )}

                {eventsToRender.map((event, index) => (
                    <MDBox key={event.id} id={index === 0 ? "event-card-0" : undefined}>
                        <EventCard
                            event={event}
                            client={clients}
                            onEdit={handleEditEvent}
                            onCancel={handleCancelEvent}
                        />
                    </MDBox>
                ))}

                {/* PAGINAÇÃO */}
                {filteredEvents.length > itemsPerPage && (
                    <MDBox display="flex" justifyContent="center" mt={4}>
                        <Pagination
                            count={Math.ceil(filteredEvents.length / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                            color={sidenavColor}
                        />
                    </MDBox>
                )}

            </MDBox>

            <CreateEventModal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                    setSelectedEvent(null);
                }}
                event={selectedEvent}
            />

        </DashboardLayout>
    );
}
